import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isEditor: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
  refreshRoles: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isEditor: false,
  isAdmin: false,
  signOut: async () => {},
  refreshRoles: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditor, setIsEditor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchRoles = useCallback(async (userId: string) => {
    try {
      const [rolesResult, editorResult] = await Promise.all([
        supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId),
        supabase
          .from("editor_profiles")
          .select("id")
          .eq("id", userId)
          .maybeSingle(),
      ]);

      const roles = rolesResult.data?.map(r => r.role) || [];
      const hasAdminRole = roles.includes("admin") || roles.includes("super_admin");
      setIsAdmin(hasAdminRole);
      setIsEditor(!hasAdminRole && roles.includes("editor") && !!editorResult.data);
    } catch (err) {
      console.error("Error fetching roles:", err);
    }
  }, []);

  const refreshRoles = useCallback(async () => {
    if (user) await fetchRoles(user.id);
  }, [user, fetchRoles]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) {
          // Defer role fetch to avoid Supabase deadlock
          setTimeout(() => fetchRoles(session.user.id), 0);
        } else {
          setIsEditor(false);
          setIsAdmin(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        fetchRoles(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchRoles]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsEditor(false);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isEditor, isAdmin, signOut, refreshRoles }}>
      {children}
    </AuthContext.Provider>
  );
};
